export interface AppUpgradeRequired {
  error: 'upgrade_required';
  minAppVersion: string;
  message: string;
}

const VERSION =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

function parseVersion(value: string) {
  const match = VERSION.exec(value);
  if (!match) return null;
  const prerelease = match[4]?.split('.') ?? [];
  if (prerelease.some((part) => /^\d+$/.test(part) && part.length > 1 && part[0] === '0')) {
    return null;
  }
  return { core: match.slice(1, 4).map(BigInt), prerelease };
}

export function isValidAppVersion(value: string): boolean {
  return parseVersion(value) !== null;
}

export function compareAppVersions(a: string, b: string): number {
  const left = parseVersion(a);
  const right = parseVersion(b);
  if (!left || !right) throw new Error('App versions must be valid semantic versions');
  for (let i = 0; i < 3; i++) {
    if (left.core[i] !== right.core[i]) return left.core[i] < right.core[i] ? -1 : 1;
  }
  if (!left.prerelease.length || !right.prerelease.length) {
    return left.prerelease.length ? -1 : right.prerelease.length ? 1 : 0;
  }
  for (let i = 0; i < Math.max(left.prerelease.length, right.prerelease.length); i++) {
    const l = left.prerelease[i];
    const r = right.prerelease[i];
    if (l === undefined) return -1;
    if (r === undefined) return 1;
    if (l === r) continue;
    const ln = /^\d+$/.test(l);
    const rn = /^\d+$/.test(r);
    if (ln && rn) return BigInt(l) < BigInt(r) ? -1 : 1;
    if (ln !== rn) return ln ? -1 : 1;
    return l < r ? -1 : 1;
  }
  return 0;
}

export function appUpgradeRequired(
  clientVersion: string | undefined,
  minimum: string
): AppUpgradeRequired | null {
  if (!isValidAppVersion(minimum)) throw new Error(`Invalid minimum app version: ${minimum}`);
  if (minimum === '0.0.0') return null;
  if (
    clientVersion &&
    isValidAppVersion(clientVersion) &&
    compareAppVersions(clientVersion, minimum) >= 0
  ) {
    return null;
  }
  return {
    error: 'upgrade_required',
    minAppVersion: minimum,
    message: 'A new version of Tupai is required to continue.',
  };
}

export function isAppVersionExempt(url: string, paths: string[]): boolean {
  const path = url.split('?')[0];
  return paths.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}
